import { Box, MenuItem, SxProps, Theme, useTheme } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ConfigContext } from "../../../context/ConfigContext";
import { useUiPreference } from "../../hooks/useUiPreference";
import { mapRoute } from "../../helpers/mapRoute";
import { SwitchToV1FeedbackDialog } from "./SwitchToV1FeedbackDialog";

interface Props {
  currentUi: "v1" | "v2";
  onClose?: () => void;
  sx?: SxProps<Theme>;
}

export function UiVersionToggleMenuItem({ currentUi, onClose, sx }: Props) {
  const { isEligible, setPreference } = useUiPreference();
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams<{ customerId: string }>();
  const config = useContext(ConfigContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation(["common"]);

  if (!isEligible || !customerId) return null;

  const label = currentUi === "v1" ? "Try new UI" : "Switch to classic UI";

  const doSwitch = (target: "v1" | "v2") => {
    setPreference(target);
    navigate(mapRoute(target, location.pathname, customerId, currentUi));
    setDialogOpen(false);
    onClose?.();
  };

  const handleClick = () => {
    if (currentUi === "v1") {
      doSwitch("v2");
      return;
    }
    if (config?.FeedbackEnabled) {
      setDialogOpen(true);
      return;
    }
    doSwitch("v1");
  };

  return (
    <>
      <MenuItem onClick={handleClick} sx={[{ display: "flex", alignItems: "center", gap: "10px" }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
        <Box component="span" sx={{ flex: 1 }}>{label}</Box>
        {currentUi === "v1" && (
          <Box
            component="span"
            sx={{
              ml: "auto",
              px: "10px",
              py: "4px",
              borderRadius: "999px",
              backgroundColor: theme.palette.primary.main,
              color: "#FFFFFF",
              fontSize: "0.625rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              lineHeight: 1.2,
            }}
          >
            {t("common:newBadge")}
          </Box>
        )}
      </MenuItem>
      <SwitchToV1FeedbackDialog
        open={dialogOpen}
        customerId={customerId}
        fromUrl={location.pathname}
        onSwitch={() => doSwitch("v1")}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}
